import {
  getDate,
  getMatchStatus,
  removeUndefinedPropertiesFromObject,
  LinkCreationService,
  getGameResult,
  getTiebreakScore,
} from '@sportal365/smp-widget-utils';
import { assetsPlaceholder } from '../../../../packages/constants/assetsPlaceholder';
import { PLAYER_ENTITY, TEAM_ENTITY } from '../../../../packages/constants/entityTypes';
import {
  INormalizedTennisMatch,
  ITennisConfig,
  ITennisMatch,
  ResultValue,
  SDKDateOptions,
  TennisParticipant,
  TennisParticipantDetails,
} from '../../../../packages/types';
import { TARGET_SELF, TARGET_BLANK } from '../../../../packages/constants/windowTarget';

interface INormalizeTennisMatchDataArgs {
  match?: ITennisMatch;
  dataEntityLinks?: ITennisConfig['dataEntityLinks'];
  dataShortStatusType?: ITennisConfig['dataShortStatusType'];
  sdkDateOptions?: SDKDateOptions;
}

interface INormalizeTennisParticipantsDataArgs {
  dataEntityLinks?: ITennisConfig['dataEntityLinks'];
  participants: TennisParticipant[];
}

interface INormalizeTennisScoreDataArgs {
  statusType?: string;
  score?: ResultValue;
}
const normalizeParticipantsProperties = ({ participants, dataEntityLinks }: INormalizeTennisParticipantsDataArgs) => {
  const linkService = dataEntityLinks && new LinkCreationService(dataEntityLinks);

  const normalizedParticipans = participants?.map((participant: TennisParticipant) => {
    const details = participant?.details.map((participantDetails: TennisParticipantDetails) => {
      return {
        name: participantDetails?.name || '-',
        logo: participantDetails?.assets?.headshot ?? assetsPlaceholder.participantPlayer,
        country: participantDetails?.country,
        id: participantDetails?.id,
        url:
          linkService?.createEntityUrl(
            String(participantDetails?.id),
            participantDetails?.entityType === TEAM_ENTITY ? TEAM_ENTITY : PLAYER_ENTITY,
            '',
            participantDetails?.slug,
          ) ?? '#',
        entityType: participantDetails?.entityType,
        slug: participantDetails?.slug,
      };
    });
    return {
      details,
      id: participant.id,
      position: participant?.position,
      winner: participant?.winner,
      type: participant?.type,
      name: participant?.name,
      currentServer: participant?.currentServer,
    };
  });

  return normalizedParticipans;
};

const normalizeScoreProperties = ({ statusType, score }: INormalizeTennisScoreDataArgs) => {
  return {
    score: {
      value: getGameResult(statusType, score?.value !== null ? score?.value : '', true),
      winner: score?.winner ?? '',
    },
    tiebreakScore: {
      value: getTiebreakScore(score?.tiebreakValue?.value),
      winner: getTiebreakScore(score?.tiebreakValue?.winner),
    },
  };
};

export const normalizeTennisMatchData = ({
  match,
  dataEntityLinks,
  dataShortStatusType,
  sdkDateOptions,
}: INormalizeTennisMatchDataArgs): INormalizedTennisMatch | undefined => {
  if (!match) return;

  const linkService = dataEntityLinks && new LinkCreationService(dataEntityLinks);
  const target = dataEntityLinks?.configuration?.newWindow ? TARGET_BLANK : TARGET_SELF;
  const matchLink =
    (match?.participants?.[0]?.id &&
      match?.participants?.[1]?.id &&
      linkService?.createMatchUrl(match.id, match?.participants[0], match?.participants[1], match.slug)) ??
    '#';

  return removeUndefinedPropertiesFromObject({
    id: match.id,
    slug: match.slug,
    participants: normalizeParticipantsProperties({ participants: match?.participants, dataEntityLinks }),
    status: getMatchStatus(match, dataShortStatusType, sdkDateOptions),
    setScores: [
      {
        home: normalizeScoreProperties({ statusType: match.status.type, score: match.results?.set1?.values[0] }),
        away: normalizeScoreProperties({ statusType: match.status.type, score: match.results?.set1?.values[1] }),
      },
      {
        home: normalizeScoreProperties({ statusType: match.status.type, score: match.results?.set2?.values[0] }),
        away: normalizeScoreProperties({ statusType: match.status.type, score: match.results?.set2?.values[1] }),
      },
      {
        home: normalizeScoreProperties({ statusType: match.status.type, score: match.results?.set3?.values[0] }),
        away: normalizeScoreProperties({ statusType: match.status.type, score: match.results?.set3?.values[1] }),
      },
      {
        home: normalizeScoreProperties({ statusType: match.status.type, score: match.results?.set4?.values[0] }),
        away: normalizeScoreProperties({ statusType: match.status.type, score: match.results?.set4?.values[1] }),
      },
      {
        home: normalizeScoreProperties({ statusType: match.status.type, score: match.results?.set5?.values[0] }),
        away: normalizeScoreProperties({ statusType: match.status.type, score: match.results?.set5?.values[1] }),
      },
    ],
    runningScore: {
      home: normalizeScoreProperties({ statusType: match.status.type, score: match.results?.runningscore?.values[0] }),
      away: normalizeScoreProperties({ statusType: match.status.type, score: match.results?.runningscore?.values[1] }),
    },
    setsWonScore: {
      home: normalizeScoreProperties({ statusType: match.status.type, score: match.results?.setswon?.values[0] }),
      away: normalizeScoreProperties({ statusType: match.status.type, score: match.results?.setswon?.values[1] }),
    },
    gameScore: {
      home: normalizeScoreProperties({ statusType: match.status.type, score: match.results?.gamescore?.values[0] }),
      away: normalizeScoreProperties({ statusType: match.status.type, score: match.results?.gamescore?.values[1] }),
    },
    finalResult: {
      home: normalizeScoreProperties({ statusType: match.status.type, score: match.results?.finalresult?.values[0] }),
      away: normalizeScoreProperties({ statusType: match.status.type, score: match.results?.finalresult?.values[1] }),
    },
    coverage: match.coverage,
    matchStatus: match.status,
    date: match?.scheduledStartTime && getDate(match?.scheduledStartTime, sdkDateOptions),
    matchLink,
    urlTarget: target,
    type: match?.type,
    odds: match.odds,
    round: match?.round,
  });
};
